import React from 'react';
import { WsDialog, WsDialogSize } from 'wisestep-base';

type ProviderContext = readonly [(option: DialogOption) => void, () => void];

const EMPTY_FUNC = () => {};
const DialogContext = React.createContext<ProviderContext>([EMPTY_FUNC, EMPTY_FUNC]);
export const useDialog = () => React.useContext(DialogContext);

type DialogParams = {
  children: React.ReactNode;
  open: boolean;
  size?: WsDialogSize;
  onClose?: () => void;
  onExited?: () => void;
};
type DialogOption = Omit<DialogParams, 'open'>;

const DialogProvider = ({ children }) => {
  const [dialogs, setDialogs] = React.useState<DialogParams[]>([]);
  const createDialog = (option: DialogOption) => {
    const dialog = { ...option, open: true };
    setDialogs((dialogs) => [...dialogs, dialog]);
  };
  const closeDialog = () => {
    setDialogs((dialogs) => {
      const latestDialog = dialogs.pop();
      if (!latestDialog) return dialogs;
      if (latestDialog.onClose) latestDialog.onClose();
      return [...dialogs].concat({ ...latestDialog, open: false });
    });
  };
  const contextValue = React.useRef([createDialog, closeDialog] as const);

  return (
    <DialogContext.Provider value={contextValue.current}>
      {children}
      {dialogs.map((dialog, i) => (
        <WsDialog
          key={i}
          openDialog={dialog.open}
          onClose={closeDialog}
          size={dialog.size}
          zIndex="1401"
        >
          {dialog.children}
        </WsDialog>
      ))}
    </DialogContext.Provider>
  );
};

export default DialogProvider;
